import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  attendanceAdjustmentsApiErrorMessage,
  createAttendanceAdjustments,
  listAttendanceAdjustments,
  type AttendanceAdjustmentEffective,
  type AttendanceAdjustmentRow,
  type AttendanceAdjustmentType,
} from "@/lib/hr/attendanceAdjustmentsStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0–12
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i); // 0–59

type TimeOption = { value: string; label: string; search: string };

type DraftLine = {
  adjustmentType: AttendanceAdjustmentType;
  hoursPart: string;
  minutesPart: string;
  reason: string;
  startOtType: AttendanceAdjustmentType;
  startOtHours: string;
  startOtMinutes: string;
  endOtType: AttendanceAdjustmentType;
  endOtHours: string;
  endOtMinutes: string;
};

function emptyDraft(): DraftLine {
  return {
    adjustmentType: "add",
    hoursPart: "0",
    minutesPart: "0",
    reason: "",
    startOtType: "add",
    startOtHours: "0",
    startOtMinutes: "0",
    endOtType: "add",
    endOtHours: "0",
    endOtMinutes: "0",
  };
}

function clampMinutes(total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.min(779, Math.round(Math.abs(total)));
}

function minutesToParts(totalMinutes: number): { hoursPart: string; minutesPart: string } {
  const total = clampMinutes(totalMinutes);
  return {
    hoursPart: String(Math.floor(total / 60)),
    minutesPart: String(total % 60),
  };
}

function signedToTypeAndParts(signedMinutes: number): {
  type: AttendanceAdjustmentType;
  hoursPart: string;
  minutesPart: string;
} {
  const type: AttendanceAdjustmentType = signedMinutes < 0 ? "deduct" : "add";
  const parts = minutesToParts(Math.abs(signedMinutes));
  return { type, ...parts };
}

function reasonFromRows(rows: AttendanceAdjustmentRow[]): string {
  for (const row of rows) {
    const r = row.reason?.trim();
    if (r) return r;
  }
  return "";
}

function draftFromExisting(params: {
  enableSplitOt: boolean;
  rows: AttendanceAdjustmentRow[];
  effective: AttendanceAdjustmentEffective;
}): DraftLine {
  const base = emptyDraft();
  base.reason = reasonFromRows(params.rows);

  if (params.enableSplitOt) {
    const start = signedToTypeAndParts(params.effective.startOtMinutes);
    const end = signedToTypeAndParts(params.effective.endOtMinutes);
    return {
      ...base,
      startOtType: start.type,
      startOtHours: start.hoursPart,
      startOtMinutes: start.minutesPart,
      endOtType: end.type,
      endOtHours: end.hoursPart,
      endOtMinutes: end.minutesPart,
    };
  }

  const general = signedToTypeAndParts(params.effective.minutes);
  return {
    ...base,
    adjustmentType: general.type,
    hoursPart: general.hoursPart,
    minutesPart: general.minutesPart,
  };
}

function parseDurationParts(hoursPart: string, minutesPart: string): number | null {
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (
    !Number.isInteger(hours) ||
    hours < 0 ||
    hours > 12 ||
    !Number.isInteger(minutes) ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return hours * 60 + minutes;
}

function SearchableTimePartPicker({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TimeOption[];
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [cycle, setCycle] = useState(0);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (next) setCycle((c) => c + 1);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-9 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
      >
        <Command key={cycle} shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-56">
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = value === opt.value;
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.search}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type AttendanceAdjustmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  employeeName: string | null;
  adjustmentDate: string;
  /** When true (work policy `enable_split_ot`), show Start OT / End OT cards. */
  enableSplitOt?: boolean;
  canCreate: boolean;
  /** Called after create so the parent can refresh daily nets. */
  onChanged?: () => void;
};

export function AttendanceAdjustmentDialog({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  adjustmentDate,
  enableSplitOt = false,
  canCreate,
  onChanged,
}: AttendanceAdjustmentDialogProps) {
  const [draft, setDraft] = useState<DraftLine>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const hourOptions = useMemo<TimeOption[]>(
    () =>
      HOUR_OPTIONS.map((h) => ({
        value: String(h),
        label: String(h),
        search: `${h} ${h}h hours hour`,
      })),
    [],
  );
  const minuteOptions = useMemo<TimeOption[]>(
    () =>
      MINUTE_OPTIONS.map((m) => ({
        value: String(m),
        label: String(m),
        search: `${m} ${m}m minutes minute`,
      })),
    [],
  );

  const loadExisting = useCallback(async () => {
    if (!employeeId || !adjustmentDate) {
      setDraft(emptyDraft());
      setIsEditing(false);
      return;
    }
    setLoadingExisting(true);
    try {
      const result = await listAttendanceAdjustments({ employeeId, adjustmentDate });
      if (result.rows.length > 0) {
        setIsEditing(true);
        setDraft(
          draftFromExisting({
            enableSplitOt,
            rows: result.rows,
            effective: result.effective,
          }),
        );
      } else {
        setIsEditing(false);
        setDraft(emptyDraft());
      }
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
      setIsEditing(false);
      setDraft(emptyDraft());
    } finally {
      setLoadingExisting(false);
    }
  }, [employeeId, adjustmentDate, enableSplitOt]);

  useEffect(() => {
    if (!open) return;
    void loadExisting();
  }, [open, loadExisting]);

  const close = () => {
    if (saving || loadingExisting) return;
    onOpenChange(false);
  };

  const save = async () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create attendance adjustments.");
      return;
    }

    if (enableSplitOt) {
      const startOt = parseDurationParts(draft.startOtHours, draft.startOtMinutes);
      const endOt = parseDurationParts(draft.endOtHours, draft.endOtMinutes);
      if (startOt == null || endOt == null) {
        toast.error("Select valid hours (0–12) and minutes (0–59) for Start OT and End OT.");
        return;
      }
      if (startOt < 1 && endOt < 1) {
        toast.error("Enter at least 1 minute for Start OT or End OT.");
        return;
      }

      setSaving(true);
      try {
        const result = await createAttendanceAdjustments({
          employeeId,
          adjustmentDate,
          lines: [
            {
              startOtMinutes: startOt,
              endOtMinutes: endOt,
              startOtAdjustmentType: draft.startOtType,
              endOtAdjustmentType: draft.endOtType,
              reason: draft.reason.trim() || null,
            },
          ],
        });
        toast.success(result.message);
        onChanged?.();
        onOpenChange(false);
      } catch (e) {
        toast.error(attendanceAdjustmentsApiErrorMessage(e));
      } finally {
        setSaving(false);
      }
      return;
    }

    const totalMinutes = parseDurationParts(draft.hoursPart, draft.minutesPart);
    if (totalMinutes == null) {
      toast.error("Select valid hours (0–12) and minutes (0–59).");
      return;
    }
    if (totalMinutes < 1) {
      toast.error("Select at least 1 minute.");
      return;
    }

    setSaving(true);
    try {
      const result = await createAttendanceAdjustments({
        employeeId,
        adjustmentDate,
        lines: [
          {
            adjustmentType: draft.adjustmentType,
            minutes: totalMinutes,
            reason: draft.reason.trim() || null,
          },
        ],
      });
      toast.success(result.message);
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden border-border/60 shadow-2xl">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border/50 shrink-0 space-y-1">
          <DialogTitle className="text-lg font-display font-bold tracking-tight">
            {isEditing ? "Edit attendance adjustment" : "Attendance adjustment"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {enableSplitOt
              ? "Choose + or - independently for Start OT and End OT."
              : isEditing
                ? "Update the adjustment for this employee on the selected date."
                : "Add or deduct time for this employee on the selected date."}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Employee</Label>
              <Input className="glass-input h-9" value={employeeName?.trim() || "—"} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input className="glass-input h-9" value={adjustmentDate} disabled readOnly />
            </div>
          </div>

          {loadingExisting ? (
            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-8 text-center text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading adjustment…
              </span>
            </div>
          ) : canCreate ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {isEditing ? "Current adjustment" : "New adjustment"}
              </p>

              {enableSplitOt ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-border/60 bg-background p-3 shadow-sm space-y-3">
                      <p className="text-sm font-medium text-foreground">Start OT</p>
                      <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Type</Label>
                          <Select
                            value={draft.startOtType}
                            onValueChange={(v) =>
                              setDraft((d) => ({ ...d, startOtType: v as AttendanceAdjustmentType }))
                            }
                          >
                            <SelectTrigger className="glass-input h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="add">+</SelectItem>
                              <SelectItem value="deduct">-</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Hours</Label>
                          <SearchableTimePartPicker
                            value={draft.startOtHours}
                            onChange={(v) => setDraft((d) => ({ ...d, startOtHours: v }))}
                            options={hourOptions}
                            placeholder="Hours"
                            searchPlaceholder="Search hours…"
                            disabled={saving || loadingExisting}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Minutes</Label>
                          <SearchableTimePartPicker
                            value={draft.startOtMinutes}
                            onChange={(v) => setDraft((d) => ({ ...d, startOtMinutes: v }))}
                            options={minuteOptions}
                            placeholder="Minutes"
                            searchPlaceholder="Search minutes…"
                            disabled={saving || loadingExisting}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border/60 bg-background p-3 shadow-sm space-y-3">
                      <p className="text-sm font-medium text-foreground">End OT</p>
                      <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)] gap-2">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Type</Label>
                          <Select
                            value={draft.endOtType}
                            onValueChange={(v) =>
                              setDraft((d) => ({ ...d, endOtType: v as AttendanceAdjustmentType }))
                            }
                          >
                            <SelectTrigger className="glass-input h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="add">+</SelectItem>
                              <SelectItem value="deduct">-</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Hours</Label>
                          <SearchableTimePartPicker
                            value={draft.endOtHours}
                            onChange={(v) => setDraft((d) => ({ ...d, endOtHours: v }))}
                            options={hourOptions}
                            placeholder="Hours"
                            searchPlaceholder="Search hours…"
                            disabled={saving || loadingExisting}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Minutes</Label>
                          <SearchableTimePartPicker
                            value={draft.endOtMinutes}
                            onChange={(v) => setDraft((d) => ({ ...d, endOtMinutes: v }))}
                            options={minuteOptions}
                            placeholder="Minutes"
                            searchPlaceholder="Search minutes…"
                            disabled={saving || loadingExisting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/60 bg-background p-3 shadow-sm">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Reason</Label>
                      <Input
                        className="glass-input h-9 text-sm"
                        value={draft.reason}
                        onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                        placeholder="Optional reason"
                        maxLength={255}
                        disabled={saving || loadingExisting}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-border/60 overflow-hidden bg-background shadow-sm">
                  <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-[7rem_5.5rem_5.5rem_minmax(0,1fr)]">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Select
                        value={draft.adjustmentType}
                        onValueChange={(v) =>
                          setDraft((d) => ({ ...d, adjustmentType: v as AttendanceAdjustmentType }))
                        }
                      >
                        <SelectTrigger className="glass-input h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add">+</SelectItem>
                          <SelectItem value="deduct">-</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Hours</Label>
                      <SearchableTimePartPicker
                        value={draft.hoursPart}
                        onChange={(v) => setDraft((d) => ({ ...d, hoursPart: v }))}
                        options={hourOptions}
                        placeholder="Hours"
                        searchPlaceholder="Search hours…"
                        disabled={saving || loadingExisting}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Minutes</Label>
                      <SearchableTimePartPicker
                        value={draft.minutesPart}
                        onChange={(v) => setDraft((d) => ({ ...d, minutesPart: v }))}
                        options={minuteOptions}
                        placeholder="Minutes"
                        searchPlaceholder="Search minutes…"
                        disabled={saving || loadingExisting}
                      />
                    </div>
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs text-muted-foreground">Reason</Label>
                      <Input
                        className="glass-input h-9 text-sm"
                        value={draft.reason}
                        onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                        placeholder="Optional reason"
                        maxLength={255}
                        disabled={saving || loadingExisting}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter className="px-4 py-2.5 border-t border-border/50 shrink-0 bg-muted/15 gap-2">
          <Button
            type="button"
            variant="glass"
            size="sm"
            className="h-8"
            onClick={close}
            disabled={saving || loadingExisting}
          >
            Close
          </Button>
          {canCreate ? (
            <Button
              type="button"
              variant="premium"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => void save()}
              disabled={saving || loadingExisting}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {isEditing ? "Update" : "Save"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
